import React, { useEffect, useState } from 'react';
import { makeStyles, Modal, Backdrop, Fade, LinearProgress } from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000
  },
  paper: {
    borderRadius: '8px',
    padding: theme.spacing(2),
    textAlign: 'center',
    maxWidth: '300px',
    width: '100%',
    position: 'relative',
  },
  success: {
    backgroundColor: '#e0f7e9',
    color: '#2e7d32',
  },
  error: {
    backgroundColor: '#ffebee',
    color: '#c62828',
  },
  fail: {
    backgroundColor: '#fff8e1',
    color: '#f57c00',
  },
  icon: {
    fontSize: '3rem',
    marginBottom: '0.5rem',
  },
  message: {
    marginBottom: '1rem',
  },
  progress: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '5px',
  },
  barSuccess: { backgroundColor: '#006400' },
  barError: { backgroundColor: '#ff0000' },
  barFail: { backgroundColor: '#ff8c00' },

  colorPrimarySuccess: { backgroundColor: '#e0f7e9' },
  colorPrimaryError: { backgroundColor: '#ffebee' },
  colorPrimaryFail: { backgroundColor: '#fff8e1' },
}));

export default function CustomAlert({
  open = false,
  message = {},
  type = 'success',
  onClose = () => {}
}) {
  const classes = useStyles();
  const [progress, setProgress] = useState(100);

  const title = message?.title || '';
  const body = message?.body || '';

  useEffect(() => {
    if (open) {
      setProgress(100);

      const timer = setTimeout(() => {
        onClose && onClose();
      }, 5000);

      const interval = setInterval(() => {
        setProgress((prev) => (prev > 0 ? prev - 0.2 : 0));
      }, 10);

      return () => {
        clearTimeout(timer);
        clearInterval(interval);
      };
    }
  }, [open, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleOutlineIcon className={classes.icon} />;
      case 'error':
        return <ErrorOutlineIcon className={classes.icon} />;
      case 'fail':
        return <ReportProblemOutlinedIcon className={classes.icon} />;
      default:
        return null;
    }
  };

  const progressBarClass = {
    success: classes.barSuccess,
    error: classes.barError,
    fail: classes.barFail,
  }[type] || classes.barSuccess;

  const colorPrimaryClass = {
    success: classes.colorPrimarySuccess,
    error: classes.colorPrimaryError,
    fail: classes.colorPrimaryFail,
  }[type] || classes.colorPrimarySuccess;

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      style={{ zIndex: 2000 }}
    >
      <Fade in={open}>
        <div className={`${classes.paper} ${classes[type] || ''}`}>
          {getIcon()}

          <h2
            id="transition-modal-title"
            style={{ fontSize: '1.25rem', margin: '0.5rem 0' }}
          >
            {title}
          </h2>

          <p
            id="transition-modal-description"
            className={classes.message}
            style={{ fontSize: '0.875rem', margin: '0.5rem 0' }}
          >
            {body}
          </p>

          <LinearProgress
            className={classes.progress}
            classes={{
              colorPrimary: colorPrimaryClass,
              barColorPrimary: progressBarClass,
            }}
            variant="determinate"
            value={progress}
          />
        </div>
      </Fade>
    </Modal>
  );
}
